// Modal de "función exclusiva" — se muestra cuando el usuario intenta usar
// una función que su plan no permite (p. ej. plan gratis intentando generar
// catálogo IA / agente, o gratis/emprendimiento intentando generar video).
//
// A diferencia de `SubscriptionLimitModal` (que es sobre créditos agotados),
// este es sobre GATING POR PLAN: el CTA lleva a la pantalla de planes para
// mejorar la suscripción.

import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';

export interface FeatureLockedModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Explicación de la restricción (texto o nodos). */
  message: React.ReactNode;
  /** Texto del CTA principal. Default "Ver planes". */
  ctaLabel?: string;
}

export const FeatureLockedModal: React.FC<FeatureLockedModalProps> = ({
  open,
  onClose,
  title,
  message,
  ctaLabel = 'Ver planes',
}) => {
  const navigate = useNavigate();
  if (!open) return null;
  // Portaleado a document.body: este modal se invoca desde páginas que viven
  // dentro del shell `z-30 transform-gpu` del App; inline, su z-[9999] queda
  // atrapado bajo la barra de navegación inferior (z-40 raíz). En el body vive
  // en el contexto raíz y aparece sobre todo.
  return createPortal(
    <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-surface-card rounded-lg shadow-md w-full max-w-md mx-4">
        <div className="p-5">
          <div className="flex justify-center mb-4">
            <span className="w-12 h-12 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
              <Lock className="w-6 h-6 text-brand-primary" />
            </span>
          </div>
          <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2 text-center">
            {title}
          </h3>
          <p className="text-[15px] text-neutral-600 mb-6 text-center">
            {message}
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/subscription');
              }}
              className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FeatureLockedModal;
